import { ExtraExpensesApiService } from '@adminServices';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '@shared';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as ExtraExpensesActions from './extra-expenses.actions';
import { selectExtraExpensesParams } from './extra-expenses.selectors';

@Injectable()
export class ExtraExpensesEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private extraExpensesApi = inject(ExtraExpensesApiService);
  private notification = inject(NotificationService);

  loadExpenses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtraExpensesActions.loadExpenses),
      switchMap(({ params }) =>
        this.extraExpensesApi.findAll(params).pipe(
          map((response) => ExtraExpensesActions.loadExpensesSuccess({ response })),
          catchError((error: any) => {
            this.notification.error('admin.extra_expenses.error');
            return of(ExtraExpensesActions.loadExpensesFailure({
              error: error.message || 'Failed to load expenses',
            }));
          }),
        ),
      ),
    ),
  );

  createExpense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtraExpensesActions.createExpense),
      switchMap(({ dto }) =>
        this.extraExpensesApi.create(dto).pipe(
          map((expense) => {
            this.notification.success('admin.extra_expenses.create_success');
            return ExtraExpensesActions.createExpenseSuccess({ expense });
          }),
          catchError((error: any) => {
            this.notification.error('admin.extra_expenses.error');
            return of(ExtraExpensesActions.createExpenseFailure({
              error: error.message || 'Failed to create expense',
            }));
          }),
        ),
      ),
    ),
  );

  updateExpense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtraExpensesActions.updateExpense),
      switchMap(({ id, dto }) =>
        this.extraExpensesApi.update(id, dto).pipe(
          map((expense) => {
            this.notification.success('admin.extra_expenses.update_success');
            return ExtraExpensesActions.updateExpenseSuccess({ expense });
          }),
          catchError((error: any) => {
            this.notification.error('admin.extra_expenses.error');
            return of(ExtraExpensesActions.updateExpenseFailure({
              error: error.message || 'Failed to update expense',
            }));
          }),
        ),
      ),
    ),
  );

  deleteExpense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExtraExpensesActions.deleteExpense),
      switchMap(({ id }) =>
        this.extraExpensesApi.delete(id).pipe(
          map(() => {
            this.notification.success('admin.extra_expenses.delete_success');
            return ExtraExpensesActions.deleteExpenseSuccess({ id });
          }),
          catchError((error: any) => {
            this.notification.error('admin.extra_expenses.error');
            return of(ExtraExpensesActions.deleteExpenseFailure({
              error: error.message || 'Failed to delete expense',
            }));
          }),
        ),
      ),
    ),
  );

  reloadAfterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ExtraExpensesActions.createExpenseSuccess,
        ExtraExpensesActions.updateExpenseSuccess,
        ExtraExpensesActions.deleteExpenseSuccess,
      ),
      withLatestFrom(this.store.select(selectExtraExpensesParams)),
      map(([ , params ]) => ExtraExpensesActions.loadExpenses({ params })),
    ),
  );
}
